import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useDueDiligence } from "../useDueDiligence";
import { isErr } from "../../Utils/Result";

export const HIDDEN_SECTIONS_SAVE_DEBOUNCE_MS = 1500;

interface HiddenPdfSections {
  hiddenSections: string[];
  setHiddenSections: Dispatch<SetStateAction<string[]>>;
  initialize: (fromServer: string[]) => void;
}

export const useHiddenPdfSections = (caseAlias: string | undefined): HiddenPdfSections => {
  const { saveHiddenPdfSections } = useDueDiligence();

  const [hiddenSections, setHiddenSections] = useState<string[]>([]);
  const initializedRef = useRef(false);
  const savedRef = useRef<string | null>(null);
  const pendingRef = useRef<{ caseAlias: string; sections: string[] } | null>(null);
  const saveRef = useRef(saveHiddenPdfSections);
  useEffect(() => { saveRef.current = saveHiddenPdfSections; });

  useEffect(() => {
    setHiddenSections([]);
    initializedRef.current = false;
    savedRef.current = null;
    pendingRef.current = null;
  }, [caseAlias]);

  const initialize = useCallback((fromServer: string[]) => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    savedRef.current = JSON.stringify(fromServer);
    setHiddenSections(fromServer);
  }, []);

  useEffect(() => {
    if (!caseAlias || !initializedRef.current) return;
    const serialized = JSON.stringify(hiddenSections);
    if (serialized === savedRef.current) return;

    const sections = hiddenSections;
    pendingRef.current = { caseAlias, sections };
    const timer = setTimeout(async () => {
      pendingRef.current = null;
      savedRef.current = serialized;
      const result = await saveRef.current(caseAlias, sections);
      if (isErr(result)) savedRef.current = null;
    }, HIDDEN_SECTIONS_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [caseAlias, hiddenSections]);

  useEffect(() => () => {
    const pending = pendingRef.current;
    if (pending) saveRef.current(pending.caseAlias, pending.sections);
  }, []);

  return { hiddenSections, setHiddenSections, initialize };
};
